import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import {
  AnalysisSectionHeader,
  ValuationComparisonChart,
  ValuationChartData,
  Icon,
  Badge,
  media,
} from '@popety-io/ui'
import {
  EstimateLogValuation,
  MultiProviderValuation,
  ValuationMethod,
  Valuation,
} from '@popety-io/types'
import { useManualValuationData } from './Valuations/hooks/useManualValuationData'
import { formatDistanceToNow } from 'date-fns'
import { useClearMapSection } from '../hooks'
import { usePublicPricePerSqm } from '../contexts'

const ContentWrapper = styled.div`
  padding: 0 24px 24px 24px;

  ${media.mobileOnly} {
    padding: 0 ${({ theme }) => theme.spacing.medium}
      ${({ theme }) => theme.spacing.medium};
  }
`

const ChartContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};

  ${media.mobileOnly} {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
  transition: all ${({ theme }) => theme.transitions.default};
  cursor: pointer;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
    border-color: ${({ theme }) => theme.colors.border.default};
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`

const ColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ $color }) => $color};
  border: 2px solid ${({ theme }) => theme.colors.background.primary};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border.light};
`

const BadgeRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

const ValueSection = styled.div`
  margin: ${({ theme }) => theme.spacing.medium} 0;
`

const PrimaryValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

const RangeValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.spacing.small};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

const ConfidenceIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

const ConfidenceDot = styled.div<{ $filled: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme, $filled }) =>
    $filled ? theme.colors.success : theme.colors.background.tertiary};
`

const ConfidenceLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: ${({ theme }) => theme.spacing.xsmall};
`

const DateLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.huge};
  color: ${({ theme }) => theme.colors.text.secondary};
`

interface ManualValuationsTabProps {
  estimateData: any
}

// Map valuation methods to colors, prioritizing custom color if provided
const getMethodColor = (
  method: ValuationMethod,
  customColor?: string
): string => {
  // Prioritize custom color if provided
  if (customColor) {
    return customColor
  }

  // Fall back to method-based color
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
      return '#FF6B6B' // Red
    case ValuationMethod.INCOME_BASED_STATIC:
      return '#4ECDC4' // Teal
    case ValuationMethod.COST_BASED_DEVELOPER:
      return '#FFE66D' // Yellow
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return '#95E1D3' // Light teal
    case ValuationMethod.COMPARABLE_SALES:
      return '#C7CEEA' // Light purple
    case ValuationMethod.CUSTOM:
      return '#F38181' // Coral (default for custom)
    default:
      return '#888' // Gray fallback
  }
}

// Map method to badge variant
const getMethodBadgeVariant = (method: ValuationMethod): string => {
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
    case ValuationMethod.COST_BASED_DEVELOPER:
      return 'cost'
    case ValuationMethod.INCOME_BASED_STATIC:
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return 'income'
    case ValuationMethod.COMPARABLE_SALES:
      return 'comparable'
    case ValuationMethod.CUSTOM:
      return 'manual'
    default:
      return 'default'
  }
}

// Get method display name
const getMethodDisplayName = (method: ValuationMethod): string => {
  switch (method) {
    case ValuationMethod.COST_BASED_INTRINSIC:
      return 'Intrinsic Value Method'
    case ValuationMethod.COST_BASED_DEVELOPER:
      return 'Developer Method'
    case ValuationMethod.INCOME_BASED_STATIC:
      return 'Static Yield Method'
    case ValuationMethod.INCOME_BASED_DYNAMIC:
      return 'DCF Method'
    case ValuationMethod.COMPARABLE_SALES:
      return 'Comparable Sales'
    case ValuationMethod.CUSTOM:
      return 'Custom Valuation'
    default:
      return 'Custom Method'
  }
}

// Get confidence label
const getConfidenceLabel = (confidence?: number): string => {
  if (!confidence) return 'Medium'
  if (confidence === 1) return 'Low'
  if (confidence === 2) return 'Medium'
  if (confidence === 3) return 'High'
  return 'Medium'
}

interface ValuationCardProps {
  valuation: Valuation
  onClick: () => void
}

const ValuationCard: FC<ValuationCardProps> = ({ valuation, onClick }) => {
  const badgeVariant = getMethodBadgeVariant(valuation.method)
  const methodName = getMethodDisplayName(valuation.method)
  const confidenceLabel = getConfidenceLabel(valuation.confidence)
  const confidence = valuation.confidence || 2
  const color = valuation.color || getMethodColor(valuation.method)

  // Format date
  const updatedAgo = formatDistanceToNow(valuation.updatedAt, {
    addSuffix: true,
  })
    .replace('about ', '')
    .replace(' ago', '')

  return (
    <Card onClick={onClick}>
      <CardHeader>
        <CardTitle>{valuation.name}</CardTitle>
        <ColorDot $color={color} title={`Chart color: ${color}`} />
      </CardHeader>

      <BadgeRow>
        <Badge variant={badgeVariant as any}>{methodName}</Badge>
      </BadgeRow>

      <ValueSection>
        <PrimaryValue>
          CHF {valuation.value.toLocaleString('de-CH')}
        </PrimaryValue>
        {valuation.range && (
          <RangeValue>
            CHF {valuation.range.min?.toLocaleString('de-CH')} - CHF{' '}
            {valuation.range.max?.toLocaleString('de-CH')}
          </RangeValue>
        )}
      </ValueSection>

      <CardFooter>
        <ConfidenceIndicator>
          <ConfidenceDot $filled={confidence >= 1} />
          <ConfidenceDot $filled={confidence >= 2} />
          <ConfidenceDot $filled={confidence >= 3} />
          <ConfidenceLabel>{confidenceLabel}</ConfidenceLabel>
        </ConfidenceIndicator>
        <DateLabel>
          <Icon name="clock" size="small" />
          {updatedAgo}
        </DateLabel>
      </CardFooter>
    </Card>
  )
}

export const ManualValuationsTab: FC<ManualValuationsTabProps> = ({
  estimateData,
}) => {
  // Clear map section data when viewing manual valuations
  useClearMapSection()

  // Price-per-m² context (session-only, provided at page level)
  const { show, areas, resolveArea } = usePublicPricePerSqm()
  const basisArea = resolveArea(areas)

  // Get log_valuation and price data from estimate
  const logValuation: EstimateLogValuation | MultiProviderValuation | null =
    estimateData?.log_valuation ?? null
  const priceData = estimateData?.price ?? null

  // Use custom hook to parse manual valuation data
  const { valuations: allValuations } = useManualValuationData(
    priceData,
    logValuation
  )

  // Get hidden valuation IDs from value_edited
  const hiddenValuationIds: string[] = useMemo(
    () => estimateData?.value_edited?.hiddenValuationIds || [],
    [estimateData?.value_edited?.hiddenValuationIds]
  )

  // Filter out hidden valuations
  const valuations = useMemo(
    () =>
      allValuations.filter(
        (valuation) => !hiddenValuationIds.includes(valuation.id)
      ),
    [allValuations, hiddenValuationIds]
  )

  // Transform valuations data for the chart
  const chartData: ValuationChartData[] = useMemo(() => {
    return valuations.map((valuation) => ({
      source: valuation.source,
      name: valuation.name,
      value: valuation.value,
      confidenceLow: valuation.range?.min,
      confidenceHigh: valuation.range?.max,
      color: getMethodColor(valuation.method, valuation.color),
      area: basisArea,
    }))
  }, [valuations, basisArea])

  const handleCardClick = (_id: string) => {
    // Public estimates have no valuation detail view.
  }

  return (
    <div>
      <AnalysisSectionHeader
        title="Manual Valuations"
        subtitle="Professional valuation methods for accurate property assessment"
      />

      <ContentWrapper>
        {valuations.length === 0 ? (
          <EmptyState>
            <Icon name="file-text" size="large" />
            <p>No manual valuations available for this property.</p>
          </EmptyState>
        ) : (
          <>
            {chartData.length > 0 && (
              <ChartContainer>
                <ValuationComparisonChart
                  data={chartData}
                  currency="CHF"
                  showPricePerSqm={show}
                />
              </ChartContainer>
            )}

            <CardGrid>
              {valuations.map((valuation) => (
                <ValuationCard
                  key={valuation.id}
                  valuation={valuation}
                  onClick={() => handleCardClick(valuation.id)}
                />
              ))}
            </CardGrid>
          </>
        )}
      </ContentWrapper>
    </div>
  )
}
